import { Badge, Button, Destructive, Icon, Popover, Reason, Section, useToast } from '@efira/ui';
import { useNavigate } from 'react-router';

import {
  useActivateChannel,
  useChannels,
  useConnectLive,
  useDisconnectLive,
  usePreflight,
} from '../../api/queries';
import type { Channel } from '../../api/queries';
import { useConsoleSession } from '../session';
import { ProStatus } from '../ProStatus';
import { LIVE_WORDS } from '../liveState';
import type { LiveKind } from '../liveState';

/**
 * Подключение: аккаунт TikTok, готовность и выход в эфир.
 *
 * ГОТОВНОСТЬ НАЗЫВАЕТ ПРИЧИНУ, А НЕ ТОЛЬКО ЦВЕТ. У эталона в заголовке стоит
 * «Ready» с точкой, и это всё: когда подключение не идёт, стример смотрит на
 * зелёную точку и не понимает, чего не хватает. Мы держим ту же строчку на
 * том же месте, но она отвечает на вопрос «чего не хватает»: нет канала,
 * не открыт оверлей, игра не выбрана.
 *
 * ВЫХОД В ЭФИР - ЕДИНСТВЕННОЕ МЕСТО, ГДЕ СПРАШИВАЮТ ТАРИФ. Платная игра
 * играет в кадре целиком; отказ приходит здесь, с названием игры и одной
 * ссылкой (`DECISIONS.md`, запись о переносе ворот тарифа). Предупреждение
 * показывается ДО нажатия: отказ в момент выхода - это уже поздно.
 */
/*
 * ГЛАВНАЯ КНОПКА КОНСОЛИ - ТА, ЧТО СНИМАЕТ ТЕКУЩУЮ БЛОКИРОВКУ ЭФИРА.
 * Находка N-06 и блок 2 единицы 21. Закрыв находку 31 («симулятор весомее
 * эфира»), единица 19 сняла заливку со всего сразу, и экран с игрой
 * перестал говорить, что делать дальше. Правило по состояниям:
 *
 *   игры нет                       - главная в правой колонке: «Выбрать игру»;
 *   игра есть, аккаунта нет        - «Выбрать аккаунт TikTok» (здесь);
 *   аккаунт есть, эфира нет        - «Выйти в эфир» (здесь);
 *   эфир идёт                      - главного действия НЕТ: обрывать эфир
 *                                    главной кнопкой нельзя;
 *   тариф не пускает игру в эфир   - главного действия нет, причина и
 *                                    ссылка на тариф словами.
 *
 * Ворота «ровно одна» обходят все эти состояния
 * (`e2e/class-buttons.spec.ts`).
 */
export function Connection() {
  const channels = useChannels();

  const active = channels.data?.channels.find(
    (channel) => channel.id === channels.data?.activeChannelId,
  );

  return (
    <Section title="Подключение" id="connection" status={<Readiness />}>
      <div className="space-y-2 px-3 pb-3" data-left="connection">
        <AccountSwitcher active={active} />
        <ConnectButton />
      </div>
    </Section>
  );
}

/**
 * Индикатор готовности.
 *
 * Считается из того же ответа, что и сам выход в эфир, - иначе индикатор и
 * кнопка начинают спорить: точка зелёная, а подключение отказывает.
 */
function Readiness() {
  const preflight = usePreflight();
  const data = preflight.data;

  if (data === undefined) {
    return <span className="text-foreground/80 text-xs">Проверяем…</span>;
  }

  const kind: LiveKind =
    data.connection.status !== 'live'
      ? 'none'
      : data.connection.sourceId === 'tiktok'
        ? 'tiktok'
        : 'simulator';

  const missing = !data.channelSelected
    ? 'нет канала'
    : !data.overlayOpen
      ? 'оверлей не открыт'
      : null;

  /*
   * СИМУЛЯТОР - НЕ ЭФИР (единица 22). Статус «Симулятор» - своим видом:
   * полая точка, а не залитая цветом эфира. Иначе рядом с «Сначала
   * выберите аккаунт TikTok» стояло «В эфире» зелёным, и одно из двух было
   * ложью.
   */
  const dot =
    kind === 'tiktok'
      ? 'bg-primary'
      : kind === 'simulator'
        ? 'border-foreground/60 border'
        : missing === null
          ? 'bg-primary'
          : 'bg-warning';
  const text = kind === 'none' ? (missing ?? LIVE_WORDS.none.status) : LIVE_WORDS[kind].status;

  return (
    <span
      data-left="connection-status"
      data-live-place="connection-status"
      data-live-state={kind}
      className="flex items-center gap-2 text-xs"
    >
      <span className={`size-2 rounded-full ${dot}`} aria-hidden />
      <span className="text-foreground/80">{text}</span>
    </span>
  );
}

/**
 * Выбор аккаунта, С КОТОРОГО ПОЙДЁТ ЭФИР.
 *
 * РОЛИ РАЗДЕЛЕНЫ (решение владельца 2026-09-29, п. 3; находка 22). Здесь -
 * только ВЫБОР из уже привязанных. Добавить, удалить и назначить аккаунт
 * по умолчанию - в настройках, «Аккаунты TikTok». Прежняя редакция вела
 * «Добавить аккаунт» на прежний экран каналов, и заводить аккаунт можно
 * было в двух местах двумя разными формами.
 *
 * Выбор здесь ЗАПОМИНАЕТСЯ: это тот же признак, что «по умолчанию» в
 * настройках, - сервер хранит один активный аккаунт. Тексты обеих
 * поверхностей называют это прямо.
 *
 * Пока аккаунт не выбран, а игра уже идёт, кнопка выбора - ГЛАВНАЯ
 * консоли: именно она снимает блокировку эфира.
 */
function AccountSwitcher({ active }: { active: Channel | undefined }) {
  const channels = useChannels();
  const activate = useActivateChannel();
  const navigate = useNavigate();
  const toast = useToast();
  const session = useConsoleSession();

  const list = channels.data?.channels ?? [];
  const need = !channels.isLoading && active === undefined;
  const main = need && session.game !== null;

  return (
    <Popover
      label={need ? 'Выбрать аккаунт TikTok' : 'Аккаунт TikTok для эфира'}
      kind="menu"
      triggerVariant={main ? 'primary' : 'outline'}
      triggerClassName="w-full justify-between"
      className="w-[15.5rem] p-1"
      trigger={
        <>
          <span className="flex min-w-0 items-center gap-2">
            <Icon name="channel" size="sm" />
            <span data-left="account-current" className="truncate">
              {channels.isLoading
                ? 'Загрузка…'
                : active !== undefined
                  ? `@${active.handle}`
                  : 'Выбрать аккаунт TikTok'}
            </span>
          </span>
          <Icon name="chevronDown" size="sm" />
        </>
      }
    >
      <p className="text-foreground/80 px-3 py-2 text-sm" data-left="account-role">
        {list.length === 0
          ? 'Ни одного аккаунта не привязано. Без него эфир не запустить — симулятор работает и без привязки.'
          : 'С какого аккаунта пойдёт эфир. Выбор запомнится и станет аккаунтом по умолчанию.'}
      </p>

      {list.map((channel) => (
        <Button
          variant="ghost"
          className="flex w-full items-center gap-2 px-3 py-2 text-start"
          key={channel.id}
          data-left-account={channel.handle}
          data-popover-close
          onClick={() => {
            if (channel.id === channels.data?.activeChannelId) return;

            activate.mutate(channel.id, {
              onSuccess: () => {
                toast.show({ tone: 'success', title: `Эфир пойдёт с @${channel.handle}` });
              },
            });
          }}
        >
          <Icon name="channel" size="sm" />
          <span data-proper className="min-w-0 flex-1 truncate">
            @{channel.handle}
          </span>
          {channel.id === channels.data?.activeChannelId && (
            <Badge variant="secondary" data-left-account-default>
              по умолчанию
            </Badge>
          )}
        </Button>
      ))}

      {/*
        Список ведут НАСТРОЙКИ. Подпись называет, что там делают, а не
        обещает «добавить» здесь же.
      */}
      <Button
        variant="ghost"
        className="flex w-full items-center gap-2 px-3 py-2 text-start"
        data-left="account-add"
        data-popover-close
        onClick={() => {
          void navigate('/app/settings/tiktok');
        }}
      >
        <Icon name="settings" size="sm" />
        <span className="flex-1">
          {list.length === 0
            ? 'Добавить аккаунт в настройках'
            : 'Добавить или удалить — в настройках'}
        </span>
      </Button>
    </Popover>
  );
}

/**
 * Кнопка выхода в эфир.
 *
 * Причина отказа приходит с сервера и показывается как есть: правило проекта
 * «текст ошибки соответствует причине». Кнопка не прячется, когда выйти
 * нельзя, - она объясняет, чего не хватает: спрятанная кнопка не учит, а
 * оставляет искать.
 */
function ConnectButton() {
  const preflight = usePreflight();
  const connect = useConnectLive();
  const disconnect = useDisconnectLive();
  const toast = useToast();
  const navigate = useNavigate();
  const session = useConsoleSession();

  const data = preflight.data;
  const connection = data?.connection;
  const liveOnTikTok = connection?.status === 'live' && connection.sourceId === 'tiktok';
  const blocked = data?.planBlocksGame ?? null;

  if (liveOnTikTok) {
    /* Эфир идёт - главного действия нет. Отключение - разрушающее
       действие: вид предупреждающий, подтверждение вторым нажатием. */
    return (
      <Destructive
        id="disconnect-live"
        solid
        className="w-full"
        data={{ 'data-left': 'disconnect-tiktok' }}
        disabled={disconnect.isPending}
        icon={<Icon name="stop" size="sm" />}
        label="Отключить эфир"
        title="Отключить эфир?"
        confirm="Отключить"
        onConfirm={() => {
          disconnect.mutate(undefined, {
            onSuccess: () => {
              toast.show({ tone: 'success', title: 'Эфир отключён' });
            },
          });
        }}
      >
        Оверлей перестанет получать комментарии и подарки. Игра на оверлее останется, эфир в TikTok
        не прервётся — подключиться можно снова.
      </Destructive>
    );
  }

  const reason =
    data === undefined
      ? 'Проверяем готовность…'
      : !data.channelSelected
        ? 'Сначала выберите аккаунт TikTok'
        : blocked !== null
          ? `«${blocked.title}» выходит в эфир на тарифе Pro`
          : null;

  /* Главная - только когда больше ничего не мешает и игра выбрана: без
     игры главное действие консоли - «Выбрать игру» в правой колонке. */
  const main = reason === null && session.game !== null;

  return (
    <div className="space-y-2">
      {/*
        ЗАБЛОКИРОВАННАЯ ГЛАВНАЯ КНОПКА - НЕ ЗАЛИТАЯ: это делает сам `Button`
        (правило `UI-10`), причина словами под ней связана `aria-describedby`.
      */}
      <Button
        variant={main ? 'primary' : 'secondary'}
        size="s"
        className="w-full"
        data-left="connect-tiktok"
        loading={connect.isPending}
        disabled={data === undefined || !data.channelSelected}
        aria-describedby={reason === null ? undefined : 'connect-why'}
        onClick={() => {
          connect.mutate(
            { mode: 'tiktok' },
            {
              onSuccess: () => {
                toast.show({ tone: 'success', title: 'Подключаемся к эфиру' });
              },
              onError: (error: unknown) => {
                const payload = error as { reason?: string; title?: string; detail?: string };

                toast.show({
                  tone: 'error',
                  title: payload.title ?? 'Не удалось подключиться',
                  ...(payload.detail === undefined ? {} : { text: payload.detail }),
                });
              },
            },
          );
        }}
      >
        {connect.isPending ? null : <Icon name="live" size="sm" />}
        {connect.isPending ? 'Подключаемся…' : 'Выйти в эфир'}
      </Button>

      {reason !== null && (
        <Reason id="connect-why" data-left="connect-reason">
          {reason}
          {blocked !== null && (
            <>
              {' '}
              <ProStatus />{' '}
              <Button
                variant="ghost"

                /* Не `link-primary`: primary на белом при 12 px даёт 2,6:1 и
                   не проходит AA. Ссылку опознают подчёркивание и вес, а не
                   цвет - решение уже принято по `.text-primary` в шапке. */
                onClick={() => {
                  void navigate('/app/plan?from=billing');
                }}
              >
                Открыть тариф
              </Button>
            </>
          )}
        </Reason>
      )}
    </div>
  );
}
